import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import Lottie from 'react-lottie';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom,
  Skeleton,
  Alert,
  Container,
  Stack,
  Divider,
  alpha,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  Diamond as DiamondIcon,
  LocalOffer as TagIcon,
  WorkspacePremium as CrownIcon,
  ShoppingCart as CartIcon,
  Save as SaveIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon
} from '@mui/icons-material';
import config from '../config';
import { faL } from '@fortawesome/free-solid-svg-icons';
import processingAnimation from '../animations/processing.json';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TemplatePicker = ({ onTemplateSelect, uuid, reloadTrigger }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [numPages, setNumPages] = useState(null);
  const [purchasedTemplates, setPurchasedTemplates] = useState([]);
  const [pageWidth, setPageWidth] = useState(300);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formDataHash, setFormDataHash] = useState('');
  const [currentSlideIndex, setCurrentSlideIndex] = useState({}); // Track slide index per template
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [userManuallyChangedTemplate, setUserManuallyChangedTemplate] = useState(false);

  // Helper function to read query parameters (works with both regular and hash routing)
  const getQueryParam = (paramName) => {
    // Try regular search params first
    const urlParams = new URLSearchParams(window.location.search);
    let value = urlParams.get(paramName);
    
    // If not found and using hash router, try hash fragment
    if (!value && window.location.hash) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        value = hashParams.get(paramName);
      }
    }
    
    return value;
  };
  
  // Engaging loading messages
  const loadingMessages = [
    "Nice Choice! ✨",
    "Preparing your biodata...",
    "Doing magic... 🪄",
    "Almost there...",
    "Creating something beautiful...",
  ];

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/sb/api/biodata/template-categories`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        
        // Handle different response formats
        let categoriesList = [];
        if (Array.isArray(data)) {
          categoriesList = data;
        } else if (data?.data && Array.isArray(data.data)) {
          categoriesList = data.data;
        } else if (data?.categories && Array.isArray(data.categories)) {
          categoriesList = data.categories;
        } else if (typeof data === 'object' && data !== null) {
          // If it's an object with string keys, extract values
          categoriesList = Object.values(data).filter(v => typeof v === 'string');
        }
        
        console.log('Fetched categories:', categoriesList);
        setCategories(categoriesList || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        // Set empty array on error so filter still shows with "All Categories"
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch templates based on selected category
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        let url;
        
        if (selectedCategory === 'all') {
          url = `${config.apiBaseUrl}/sb/api/biodata/templates`;
        } else {
          url = `${config.apiBaseUrl}/sb/api/biodata/templatesByCategory?tag=${selectedCategory}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const responseData = await response.json();

        // Handle different response formats
        let templatesList = [];
        if (Array.isArray(responseData)) {
          templatesList = responseData;
        } else if (responseData?.data && Array.isArray(responseData.data)) {
          templatesList = responseData.data;
        } else if (responseData?.templates && Array.isArray(responseData.templates)) {
          templatesList = responseData.templates;
        } else if (responseData?.results && Array.isArray(responseData.results)) {
          templatesList = responseData.results;
        } else {
          console.warn('Unexpected API response format:', responseData);
          templatesList = [];
        }

        if (!Array.isArray(templatesList)) {
          throw new Error('Templates data is not an array');
        }

        const sortedData = [...templatesList].sort((a, b) => a.name.localeCompare(b.name));
        const formattedData = sortedData.map(t => {
          // Priority: Use thumbnails array if available, ignore thumbnail field
          let thumbnails = [];
          
          if (t.thumbnails && Array.isArray(t.thumbnails) && t.thumbnails.length > 0) {
            // Use thumbnails array - ignore thumbnail field
            thumbnails = t.thumbnails.map(thumb => `${config.serverUrl}/public/biodata/thumbnails/${thumb}`);
          } else if (t.thumbnail) {
            // Fallback to single thumbnail only if thumbnails array doesn't exist or is empty
            thumbnails = [`${config.serverUrl}/public/biodata/thumbnails/${t.thumbnail}`];
          }
          
          return {
            ...t,
            imageUrl: thumbnails[0] || '', // Keep imageUrl for backward compatibility
            imageUrls: thumbnails // Array of image URLs
          };
        });

        setTemplates(formattedData);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        setTemplates([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    const fetchBiodata = async () => {
      try {
        const apiUrl = config.apiBaseUrl;
        const response = await fetch(`${apiUrl}/sb/api/mybiodata/${uuid}`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setPurchasedTemplates(data?.purchasedTemplates || []);
      } catch (err) {
        console.error("Error fetching biodata:", err);
      }
    };

    if (uuid) fetchBiodata();
  }, [uuid]);

  // Select template by templateId when templates are loaded and templateId is in query
  // Only auto-select on initial load, not when user manually changes template
  useEffect(() => {
    // Read query parameter inside useEffect to make it reactive
    const queryTemplateId = getQueryParam("templateId");
    
    // Don't auto-select if user has manually changed the template
    if (userManuallyChangedTemplate || !queryTemplateId || templates.length === 0 || loading) {
      return;
    }

    console.log('Attempting to select template by templateId:', queryTemplateId);
    console.log('Available templates:', templates.map(t => ({ id: t._id, name: t.name })));
    
    const templateToSelect = templates.find(t => {
      // Try both string comparison and ObjectId comparison
      return t._id === queryTemplateId || String(t._id) === String(queryTemplateId);
    });

    if (templateToSelect) {
      console.log('Template found:', templateToSelect.name, templateToSelect._id);
      // Only select if no template is selected, or the selected template is different
      if (!selectedTemplate || !selectedTemplate._id || String(selectedTemplate._id) !== String(queryTemplateId)) {
        console.log('Selecting template:', templateToSelect.name);
        // Check if template is purchased (purchasedTemplates may still be loading)
        const isPurchased = purchasedTemplates.some(pt => {
          const ptId = pt.templateId || pt._id || pt;
          return String(ptId) === String(templateToSelect._id);
        });
        setSelectedTemplate(templateToSelect);
        setPreviewLoading(true);
        setLoadingMessageIndex(0);
        const version = templateToSelect?.version || 'v1';
        const pdfUrl = `${config.apiBaseUrl}/generate-pdf/${uuid}/${version}?t=${templateToSelect._id}&lang=${lang}`;
        setPreviewUrl(pdfUrl);
        onTemplateSelect(templateToSelect, isPurchased);
      } else {
        console.log('Template already selected:', selectedTemplate.name);
      }
    } else {
      console.warn('Template not found with ID:', queryTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, selectedTemplate, purchasedTemplates, uuid, lang, loading, userManuallyChangedTemplate]);

  // Handle reload trigger from parent component
  useEffect(() => {
    if (reloadTrigger > 0 && selectedTemplate) {
      // Force refresh the preview when reload trigger changes
      setPreviewLoading(true);
      setLoadingMessageIndex(0); // Reset message index
      
      const version = getTemplateVersion(selectedTemplate);
      const pdfUrl = `${config.apiBaseUrl}/generate-pdf/${uuid}/${version}?t=${selectedTemplate._id}&lang=${lang}`;
      // Update URL to force Document to reload
      // Adding timestamp to ensure URL is different
      const timestampedUrl = `${pdfUrl}&_t=${Date.now()}`;
      setPreviewUrl(timestampedUrl);
      
      const isPurchased = isTemplatePurchased(selectedTemplate._id, purchasedTemplates);
      onTemplateSelect(selectedTemplate, isPurchased);
      // previewLoading will be set to false in onDocumentLoadSuccess
    }
  }, [reloadTrigger]);

  // Rotate loading messages while preview is loading
  useEffect(() => {
    if (!previewLoading) return;

    const messageInterval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(messageInterval);
  }, [previewLoading, loadingMessages.length]);

  const getTemplateVersion = (template) => {
    return template?.version || 'v1';
  };

  const getCurrentSlide = (templateId) => {
    return currentSlideIndex[templateId] || 0;
  };

  const handleSlideChange = (templateId, direction, totalSlides, e) => {
    e.stopPropagation();
    const currentSlide = getCurrentSlide(templateId);
    let newSlide;
    
    if (direction === 'next') {
      newSlide = (currentSlide + 1) % totalSlides;
    } else {
      newSlide = (currentSlide - 1 + totalSlides) % totalSlides;
    }
    
    setCurrentSlideIndex(prev => ({
      ...prev,
      [templateId]: newSlide
    }));
  };

  const handleOpenDemoPdf = (template) => {
    const url = `${config.apiBaseUrl}/sb/api/biodata/template-preview/${template._id}?lang=${lang}`;
    window.open(url, "_blank");
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPreviewLoading(false);
  };

  const handleTemplateSelect = (template) => {
    // Mark that user has manually selected a template
    setUserManuallyChangedTemplate(true);
    const isPurchased = isTemplatePurchased(template._id, purchasedTemplates);
    setSelectedTemplate(template);
    setPreviewLoading(true);
    setLoadingMessageIndex(0); // Reset message index
    const version = getTemplateVersion(template);
    const pdfUrl = `${config.apiBaseUrl}/generate-pdf/${uuid}/${version}?t=${template._id}&lang=${lang}`;
    setPreviewUrl(pdfUrl);
    onTemplateSelect(template, isPurchased);
  };

  const isTemplatePurchased = (templateId) => {
    return purchasedTemplates.some(pt => pt.templateId === templateId);
  };

  const getPriceChip = (template) => {
    const isPurchased = isTemplatePurchased(template._id);

    if (isPurchased) {
      return (
        <Chip
          icon={<DiamondIcon />}
          label="OWNED"
          color="secondary"
          variant="filled"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          }}
        />
      );
    }

    if (!template.pricing || template.pricing.isFree) {
      return (
        <Chip
          icon={<TagIcon />}
          label="FREE"
          color="success"
          variant="filled"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
          }}
        />
      );
    }

    if (template.pricing.amount > 50) {
      return (
        <Chip
          icon={<CrownIcon />}
          label={`₹${template.pricing.amount}`}
          color="warning"
          variant="filled"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
          }}
        />
      );
    }

    return (
      <Chip
        icon={<CartIcon />}
        label={`₹${template.pricing.amount}`}
        color="primary"
        variant="filled"
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          height: 20,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
            Choose Template
          </Typography>
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={6} sm={4} md={3} lg={3} key={index}>
                <Card sx={{ borderRadius: 1 }}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: '4px 4px 0 0' }} />
                  <CardContent sx={{ p: 1.5 }}>
                    <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ py: 3 }}>
          {/* Header with Filter */}
          {!selectedTemplate && (
            <Fade in={true} timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Choose Your Template
                </Typography>
                
                {/* Category Filter Dropdown */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <FormControl 
                    variant="outlined" 
                    size="small"
                    sx={{ 
                      minWidth: { xs: 200, sm: 250 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      },
                    }}
                  >
                    <InputLabel id="category-filter-label">
                      {t("filterByCategory")}
                    </InputLabel>
                    <Select
                      labelId="category-filter-label"
                      id="category-filter"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      label={t("filterByCategory")}
                      disabled={categoriesLoading}
                    >
                      <MenuItem value="all">
                        <em>{t("allCategories")}</em>
                      </MenuItem>
                      {categories.length > 0 ? (
                        categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {typeof category === 'string' 
                              ? category.charAt(0).toUpperCase() + category.slice(1)
                              : category}
                          </MenuItem>
                        ))
                      ) : !categoriesLoading && (
                        <MenuItem value="all" disabled>
                          No categories available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Fade>
          )}
          
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading templates: {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  if (templates.length === 0 && !loading) {
    return (
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ py: 3 }}>
          {/* Header with Filter */}
          {!selectedTemplate && (
            <Fade in={true} timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Choose Your Template
                </Typography>
                
                {/* Category Filter Dropdown */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <FormControl 
                    variant="outlined" 
                    size="small"
                    sx={{ 
                      minWidth: { xs: 200, sm: 250 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      },
                    }}
                  >
                    <InputLabel id="category-filter-label">
                      {t("filterByCategory")}
                    </InputLabel>
                    <Select
                      labelId="category-filter-label"
                      id="category-filter"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      label={t("filterByCategory")}
                      disabled={categoriesLoading}
                    >
                      <MenuItem value="all">
                        <em>{t("allCategories")}</em>
                      </MenuItem>
                      {categories.length > 0 ? (
                        categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {typeof category === 'string' 
                              ? category.charAt(0).toUpperCase() + category.slice(1)
                              : category}
                          </MenuItem>
                        ))
                      ) : !categoriesLoading && (
                        <MenuItem value="all" disabled>
                          No categories available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Fade>
          )}
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No templates available for this category.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different category or "All Categories".
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Fade in={true} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {!selectedTemplate && 'Choose Your Template'}
            </Typography>
            
            {/* Category Filter Dropdown */}
            {!selectedTemplate && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: 2
              }}>
                <FormControl 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    minWidth: { xs: 200, sm: 250 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                >
                  <InputLabel id="category-filter-label">
                    {t("filterByCategory")}
                  </InputLabel>
                  <Select
                    labelId="category-filter-label"
                    id="category-filter"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label={t("filterByCategory")}
                    disabled={categoriesLoading}
                  >
                    <MenuItem value="all">
                      <em>{t("allCategories")}</em>
                    </MenuItem>
                    {categories.length > 0 ? (
                      categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {typeof category === 'string' 
                            ? category.charAt(0).toUpperCase() + category.slice(1)
                            : category}
                        </MenuItem>
                      ))
                    ) : !categoriesLoading && (
                      <MenuItem value="all" disabled>
                        No categories available
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </Fade>

        {/* Templates Grid - Show only when no template is selected */}
        {!selectedTemplate && (
          <Slide direction="up" in={true} timeout={1000}>
            <Grid container spacing={{ xs: 0.5, sm: 2 }} sx={{ mb: 4 }}>
              {templates.map((template, index) => (
                <Grid item xs={6} sm={4} md={3} lg={3} key={template._id}>
                  <Zoom in={true} timeout={600 + index * 50}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'visible',
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 0,
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: 'transparent',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: '0 6px 24px rgba(255, 140, 66, 0.2)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {/* Template Image(s) */}
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '140%',
                          overflow: 'hidden',
                          borderRadius: '4px 4px 0 0',
                        }}
                      >
                        {template.imageUrls && template.imageUrls.length > 1 ? (
                          // Multiple thumbnails - show as carousel slides
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            {/* Current slide image */}
                            {(() => {
                              const currentSlide = getCurrentSlide(template._id);
                              const currentImgUrl = template.imageUrls[currentSlide];
                              
                              return (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                  }}
                                >
                                  <img
                                    src={currentImgUrl}
                                    alt={`${template.name} - Image ${currentSlide + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/300x420?text=Template+Preview';
                                    }}
                                  />
                                </Box>
                              );
                            })()}
                            
                            {/* Navigation arrows */}
                            <IconButton
                              onClick={(e) => handleSlideChange(template._id, 'prev', template.imageUrls.length, e)}
                              sx={{
                                position: 'absolute',
                                left: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: alpha(theme.palette.common.black, 0.6),
                                color: 'white',
                                width: 28,
                                height: 28,
                                zIndex: 2,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.common.black, 0.8),
                                },
                              }}
                            >
                              <ArrowBackIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            
                            <IconButton
                              onClick={(e) => handleSlideChange(template._id, 'next', template.imageUrls.length, e)}
                              sx={{
                                position: 'absolute',
                                right: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: alpha(theme.palette.common.black, 0.6),
                                color: 'white',
                                width: 28,
                                height: 28,
                                zIndex: 2,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.common.black, 0.8),
                                },
                              }}
                            >
                              <ArrowForwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            
                            {/* Slide indicator dots */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 4,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: 0.5,
                                zIndex: 2,
                              }}
                            >
                              {template.imageUrls.map((_, idx) => {
                                const currentSlide = getCurrentSlide(template._id);
                                return (
                                  <Box
                                    key={idx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentSlideIndex(prev => ({
                                        ...prev,
                                        [template._id]: idx
                                      }));
                                    }}
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: idx === currentSlide 
                                        ? 'white' 
                                        : alpha(theme.palette.common.white, 0.5),
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s',
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        ) : (
                          // Single thumbnail - original display
                          <img
                            src={template.imageUrl}
                            alt={template.name}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/300x420?text=Template+Preview';
                            }}
                          />
                        )}
                      </Box>

                      <CardContent sx={{ p: { xs: 0.5, sm: 1.5 } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontWeight: 500,
                            textAlign: 'center',
                            color: theme.palette.text.primary,
                            fontSize: '0.6rem',
                            lineHeight: 1.1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {template.name}
                        </Typography>

                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                          {getPriceChip(template)}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDemoPdf(template);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              width: 24,
                              height: 24,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Slide>
        )}

        {/* Selected Template Preview */}
{selectedTemplate && (
  <Fade in={true} timeout={600}>
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      px: { xs: 1, sm: 0 },
    }}>
      {/* Main Card */}
      <Card sx={{
        borderRadius: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'visible',
        maxWidth: 600,
        width: '100%',
        mx: { xs: 0, sm: 'auto' },
        boxShadow: `
          0 2px 4px ${alpha(theme.palette.common.black, 0.04)},
          0 4px 8px ${alpha(theme.palette.common.black, 0.03)}
        `,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        background: theme.palette.background.paper,
      }}>
        {/* Header */}
        <Box sx={{
          textAlign: 'center',
          px: { xs: 2, sm: 4 },
          pt: { xs: 2.5, sm: 4 },
          pb: { xs: 1.5, sm: 2 },
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: { xs: 0.5, sm: 1 }, 
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            <CheckIcon sx={{
              color: theme.palette.success.main,
              mr: 1,
              verticalAlign: 'middle',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }} />
            Template Selected
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {selectedTemplate.name}
          </Typography>
        </Box>

        {/* PDF Preview Container */}
        <Box sx={{
          textAlign: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 4 },
          position: 'relative',
          bgcolor: alpha(theme.palette.grey[50], 0.5),
        }}>
          {/* PDF Preview Frame - Makes PDF stand out */}
          <Box sx={{
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: { xs: 280, sm: 380 },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            minWidth: { xs: 'auto', sm: 300 },
            bgcolor: theme.palette.common.white,
            borderRadius: { xs: 1.5, sm: 2 },
            p: { xs: 1.5, sm: 3 },
            boxShadow: !previewLoading ? `
              0 2px 8px ${alpha(theme.palette.common.black, 0.15)},
              0 8px 24px ${alpha(theme.palette.common.black, 0.12)},
              inset 0 1px 0 ${alpha(theme.palette.common.white, 0.9)}
            ` : 'none',
            border: !previewLoading ? `2px solid ${alpha(theme.palette.grey[200], 0.8)}` : 'none',
            transform: 'translateZ(0)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': !previewLoading ? {
              transform: { xs: 'none', sm: 'translateY(-4px) translateZ(0)' },
              boxShadow: `
                0 4px 12px ${alpha(theme.palette.common.black, 0.18)},
                0 12px 32px ${alpha(theme.palette.common.black, 0.15)},
                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.9)}
              `,
              borderColor: alpha(theme.palette.primary.main, 0.3),
            } : {}
          }}>
            {/* Sharp PDF Content */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              overflow: 'hidden',
              borderRadius: 1,
              backgroundColor: theme.palette.common.white,
              '& .react-pdf__Page': {
                boxShadow: 'none',
                lineHeight: 0 // Fixes extra space below PDF
              },
              '& .react-pdf__Page__textContent': {
                // Ensures text remains sharp
                transform: 'translate(0) !important',
                position: 'absolute',
                top: 0,
                left: 0
              }
            }}>
              {previewUrl ? (
                <Document
                  key={previewUrl} // Force re-render when URL changes
                  file={previewUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => {
                    console.error('PDF load error:', error);
                    setPreviewLoading(false);
                  }}
                  loading={
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: { xs: 280, sm: 350 },
                      py: { xs: 2, sm: 4 },
                      px: { xs: 1, sm: 0 },
                    }}>
                      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Lottie
                          options={{
                            loop: true,
                            autoplay: true,
                            animationData: processingAnimation,
                            rendererSettings: {
                              preserveAspectRatio: 'xMidYMid slice'
                            }
                          }}
                          height={isMobile ? 80 : 120}
                          width={isMobile ? 80 : 120}
                        />
                      </Box>
                      <Fade in={true} key={loadingMessageIndex}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: { xs: 0.5, sm: 1 },
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            px: { xs: 2, sm: 0 },
                          }}
                        >
                          {loadingMessages[loadingMessageIndex]}
                        </Typography>
                      </Fade>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 2, sm: 0 },
                        }}
                      >
                        Please wait while we prepare your preview...
                      </Typography>
                    </Box>
                  }
                  error={
                    <Alert severity="error">
                      Failed to load PDF preview. Please try again.
                    </Alert>
                  }
                >
                  <Page
                    pageNumber={1}
                    width={isMobile ? 200 : isTablet ? 260 : 320}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    loading=""
                  />
                </Document>
              ) : (
                <Box sx={{ 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: 280, sm: 350 },
                  py: { xs: 2, sm: 4 },
                  px: { xs: 1, sm: 0 },
                }}>
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Lottie
                      options={{
                        loop: true,
                        autoplay: true,
                        animationData: processingAnimation,
                        rendererSettings: {
                          preserveAspectRatio: 'xMidYMid slice'
                        }
                      }}
                      height={isMobile ? 80 : 120}
                      width={isMobile ? 80 : 120}
                    />
                  </Box>
                  <Fade in={true} key={loadingMessageIndex}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: { xs: 0.5, sm: 1 },
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        px: { xs: 2, sm: 0 },
                      }}
                    >
                      {loadingMessages[loadingMessageIndex]}
                    </Typography>
                  </Fade>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 0 },
                    }}
                  >
                    Please wait while we prepare your preview...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {numPages > 1 && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Page 1 of {numPages} (showing first page only)
            </Typography>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            justifyContent="center"
            sx={{ 
              mt: { xs: 3, sm: 4 },
              pb: { xs: 2.5, sm: 4 },
              px: { xs: 2, sm: 4 },
            }}
          >
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              onClick={() => {
                // Mark that user is manually changing template
                setUserManuallyChangedTemplate(true);
                setSelectedTemplate(null);
                setPreviewUrl(null);
              }}
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                py: { xs: 1.25, sm: 0.75 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-1px)' },
                  boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              }}
            >
              Change Template
            </Button>
            <Button
              variant="contained"
              size={isMobile ? "medium" : "small"}
              startIcon={<ViewIcon />}
              onClick={() => handleOpenDemoPdf(selectedTemplate)}
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                py: { xs: 1.25, sm: 0.75 },
                background: theme.palette.primary.main,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: { xs: 'none', sm: 'translateY(-1px)' },
                  boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  background: theme.palette.primary.dark,
                }
              }}
            >
              {t("viewSample")}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  </Fade>
)}

      </Box>
    </Container>
  );
};

export default TemplatePicker;